import { Table } from 'dexie';
import { DocProfileResponse } from './api/fetchDoctorProfile';
import { his } from './pages/history';
import getDb from './store/db';
import {
    getDoc,
    getItemFromMisc,
    getVisitByIds,
    setSelectedClinicId,
    getMe,
    isDoctorSelectorPossible,
    getDoctorSelectorList,
    getMyClinics,
    amIDoctorOnly,
    MISC_KEYS,
    getAllPatientsUsingPids,
} from './store/idbHandler';
import { Visit } from './store/types/visit';
import { track } from './tracking';
import { GenieSettings } from './api/pushGenie';
import { getStore } from './App';
import { toggleGlobalSearch } from './store/actions/session';
import { getAllVisitsWithPatientIds, getPxPrintUrl, getPxSendUrl, HELB, HXNG } from './pages/utils';
import { getMyDbId, getMyIdWithBackwardCompatibility } from './api/mactiveid';
import { MyDoc, MyProfileResponse } from './api/fetchMyProfile';
import { getMActiveId } from './store/localstorage';
import { getAccessControl, showAccessDeniedError } from './store/AccessControl';
import { notification } from 'antd';
import { BizSettings, Ipd } from './api/fetchMyGenie';
import setupAppointmentListener, {
    createAppointment,
    getAppointmentById,
    queryAppointments,
    updateAppointment,
} from './firestore/appointments';
import { fetchPatientsList, fetchPtDir, getMedantaBizId } from './store/actions/app';
import {
    addDoc,
    collection,
    DocumentData,
    FieldValue,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import {
    cancelAllAppointmentReminders,
    sendAppointmentCommunication,
} from './firestore/appointments/communications';
import { initFirebaseAuth } from './firestore';
import {
    FirestoreAppointmentActivityEvent,
    getActivityLogStylesClient,
} from './utils/appointmentActivityUIRulesAndPraser';
import { LS_NEW_SIGN_IN_STRIP } from './components/common/NewSignInStrip';

class T {
    t: Table | undefined;
    constructor(t: Table) {
        this.t = t;
    }

    async get(key: string) {
        return this.t?.get(key);
    }

    bulkGet(keys: string[]) {
        return this.t?.bulkGet(keys);
    }
}

export enum AppointmentActivityType {
    CREATED = 'CREATED',
    VISIT_CHECKED_IN = 'VISIT_CHECKED_IN',
    VISIT_STARTED = 'VISIT_STARTED',
    VISIT_RESCHEDULED = 'VISIT_RESCHEDULED',
    VISIT_CANCELLED = 'VISIT_CANCELLED',
    VISIT_ENDED = 'VISIT_ENDED',
    VISIT_RESUMED = 'VISIT_RESUMED',
}

export enum SourcePlatform {
    ANDROID = 'doc-android',
    WEB = 'doc-web',
    IOS = 'doc-ios',
    IPAD = 'doc-iPad',
}

export interface firestoreAppointmentActivityDataObject {
    created_at: Timestamp | FieldValue;
    event: AppointmentActivityType;
    actor_name: string;
    actor_id: string;
    actor_role: Actor;
    client_id: SourcePlatform;
    expires_at: Timestamp; // this will the TTL, and will auto delete after 6m,
}

export enum Actor {
    DOCTOR = 'DOCTOR',
    STAFF = 'STAFF',
}

function isValidActivityEvent(data: DocumentData): data is FirestoreAppointmentActivityEvent {
    return (
        typeof data.event === 'string' &&
        typeof data.actor_name === 'string' &&
        (data.actor_role === 'DOCTOR' || data.actor_role === 'STAFF') &&
        data.created_at instanceof Timestamp
    );
}

class Bridge {
    navigate(
        type: 'push' | 'replace',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        params,
    ) {
        const store = getStore();
        const isGSVisible = store.getState().session.isGlobalSearchVisible;

        if (isGSVisible) {
            store.dispatch(toggleGlobalSearch(false));
        }

        his[type](params);
    }

    async isInBetaSchedule(): Promise<boolean> {
        return true;
        // try {
        //     const response = getMe();
        //     if (response?.doctor?.profile.professional.onboarded_for?.includes('IN')) {
        //         return true;
        //     }
        //     const { betaSchedule } = await getItemFromMisc<GenieSettings>(
        //         getMActiveId() || '',
        //         'settings',
        //     );
        //     return betaSchedule;
        // } catch (e) {
        //     return false;
        // }
    }

    getDoc(): DocProfileResponse | undefined {
        const store = getStore();
        const isHxngPatientConsent = store.getState().session.isHxngPatientConsent;
        const doc = getDoc();
        const onboarded_for = doc?.profile.professional.onboarded_for;
        if (isHxngPatientConsent) {
            if (onboarded_for && !onboarded_for.includes(HXNG)) onboarded_for.push(HXNG);
            if (onboarded_for && !onboarded_for.includes(HELB)) onboarded_for.push(HELB);
        }
        return doc;
    }

    getMe(): MyProfileResponse | undefined {
        const response = getMe();
        if (response) {
            response.docs = getDoctorSelectorList();
        }
        if (response && !response?.clinics?.length) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            response.clinics = getMyClinics()?.map((item) => ({ ...item, id: item._id }));
        }
        return response;
    }

    amIDoctorOnly(): boolean {
        return amIDoctorOnly();
    }

    isDoctorSelectorPossible(): boolean {
        return isDoctorSelectorPossible();
    }

    getDoctorSelectorList(): MyDoc[] | undefined {
        return getDoctorSelectorList();
    }

    getRxPrintUrl(id: string): string | undefined {
        return getPxPrintUrl(id);
    }

    getRxSendUrl(id: string): string | undefined {
        return getPxSendUrl(id);
    }

    changeClinic(clinicId: string) {
        setSelectedClinicId(clinicId);
    }

    async getLocalVisitByIds(ids: string[]): Promise<Visit[] | undefined> {
        return getVisitByIds(ids);
    }

    async getData(tableName: string): Promise<T | undefined> {
        const t = getDb(getMyDbId() || '')?.table(tableName);
        if (!t) {
            return undefined;
        }

        const rt = new T(t);

        return rt;
    }

    async isHmisEnabled(): Promise<boolean> {
        const settings = await getItemFromMisc<GenieSettings>(getMyDbId() || '', 'settings');

        return settings?.abdm ? true : false;
    }

    async isPrintLabelEnabled(): Promise<boolean> {
        const settings = await getItemFromMisc<GenieSettings>(
            getMyIdWithBackwardCompatibility() || '',
            'settings',
        );

        return settings?.barcodeEnabled ? true : false;
    }

    track(name: string, params?: { [k: string]: string | undefined }): void {
        track(name, params);
    }

    getIdb(id?: string) {
        return getDb(id || getMyDbId() || '');
    }

    isTreatmentPlanEnabled() {
        const store = getStore();
        const state = store.getState();
        return state.session.isTreatmentPlanEnabled;
    }

    canAccessRoute(match: string): boolean {
        return getAccessControl().canAccessRoute(match);
    }

    isAllowed(action: string): boolean {
        return getAccessControl().isAllowed(action);
    }

    isAccessAvailable(name: string): boolean {
        return getAccessControl().isAccessAvailable(name);
    }

    showAccessDeniedError(): void {
        showAccessDeniedError();
    }

    notification = notification;

    async isIpdEnabled() {
        const myId = getMActiveId();
        if (!myId) {
            return;
        }
        const ipd = await getItemFromMisc<Ipd>(myId, MISC_KEYS.ipd);
        return ipd.enabled;
    }

    async showPurchaseEkaCreditsPopover() {
        const myId = getMActiveId();
        if (!myId) {
            return true;
        }
        const { showPurchaseEkaCreditsPopover = true } = (await getItemFromMisc<BizSettings>(
            myId,
            'bizSettings',
        )) || { showPurchaseEkaCreditsPopover: true };
        return showPurchaseEkaCreditsPopover;
    }

    async showNewSignInStrip() {
        const myId = getMActiveId();
        if (!myId) {
            return true;
        }
        const bid = getMe()?.user['b-id'];
        const medantaBizId = getMedantaBizId();
        const blackListedBids = [...medantaBizId, '74117920305567'];
        const isBlackListedBusiness = blackListedBids.includes(bid ?? '');
        const isClosed = localStorage.getItem(LS_NEW_SIGN_IN_STRIP) === 'false';
        if (isBlackListedBusiness || isClosed) {
            return false;
        }
        const { showNewSignInStrip = true } = (await getItemFromMisc<BizSettings>(
            myId,
            'bizSettings',
        )) || { showNewSignInStrip: true };
        return showNewSignInStrip;
    }

    setupAppointmentListener = setupAppointmentListener;
    refreshPatientDirectory = fetchPtDir;
    fetchPatientListFromServer = fetchPatientsList;
    getPatientsByIds = getAllPatientsUsingPids;
    getAllVisitsWithPatientIds = getAllVisitsWithPatientIds;

    appointments = {
        create: createAppointment,
        update: updateAppointment,
        sendCommunication: sendAppointmentCommunication,
        cancelAllReminders: cancelAllAppointmentReminders,
        query: queryAppointments,
        get: getAppointmentById,
    };

    firestore = {
        Timestamp,
        serverTimestamp,
    };

    firebase = {
        initAuth: initFirebaseAuth,
    };

    recordAppointmentActivityInFireStore = {
        async recordAppointmentActivityInFS(
            dataObject: firestoreAppointmentActivityDataObject,
            aid: string,
        ) {
            try {
                if (!aid) return;
                if (!dataObject || !Object.keys(dataObject).length) {
                    track('appointent_activity_log_to_FS_v2_inCompleteData', {
                        AppointmentActivitydataSentToWriteToFS: JSON.stringify(dataObject),
                        errorMessage: 'the appointment activity object was empty',
                    });
                    return;
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const db = window.firestore;

                await addDoc(collection(db, 'appointments', aid, 'events'), dataObject);
            } catch (err) {
                track('appointent_activity_log_to_FS_v2_catchBlock', {
                    AppointmentActivitydataSentToWriteToFS: JSON.stringify(dataObject),
                    errorMessage: String(err),
                });
            }
        },
        async getAppointmentActivityLogs(aid: string) {
            if (!aid) {
                track('appointent_activity_log_to_FS_v2_AID_NotPassed_ToGetTheLogs', {
                    errorMessage: 'AID was not passed',
                });
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const db = window.firestore;
            try {
                const eventsRef = collection(db, 'appointments', aid, 'events');
                const eventsQuery = query(eventsRef, orderBy('created_at', 'desc'));
                const snapshot = await getDocs(eventsQuery);

                if (snapshot.empty) {
                    track('appointent_activity_log_to_FS_v2_No_logs_found', {
                        errorMessage: `no logs for this specific AID ${aid}`,
                    });
                    return [];
                }

                const logs = snapshot.docs
                    .map((doc) => {
                        const data = doc.data();
                        if (!isValidActivityEvent(data)) {
                            return null;
                        }

                        const styled = getActivityLogStylesClient(data);
                        if (!styled) return null;
                        return {
                            id: doc.id,
                            original: data,
                            styled,
                        };
                    })
                    .filter((item): item is NonNullable<typeof item> => item !== null);

                return logs;
            } catch (err) {
                track('appointment_activity_log_read_error', {
                    AID: aid,
                    errorMessage: String(err),
                });
                return [];
            }
        },
    };
}

const exposeBridge = (): void => {
    // eslint-disable-next-line no-undef, @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.Bridge = Bridge;
};

export default exposeBridge;
